/**
 * The pure half of someone's ledger: validating a line, reading an amount
 * someone typed, and adding it all up. Shared so the form and the route refuse
 * the same things, and the totals on screen are the ones a CSV export would sum to.
 */
import {
  LEDGER_CATEGORY_MAX,
  LEDGER_KINDS,
  LEDGER_NOTE_MAX,
  RECURRING_EVERY,
  type LedgerEntry,
  type LedgerInput,
  type LedgerKind,
  type Recurring,
  type RecurringInput,
} from "./types.js";

/** The ones the picker offers first. Any ISO 4217 code the runtime knows is accepted. */
export const COMMON_CURRENCIES = ["GBP", "EUR", "USD", "CAD", "AUD", "NZD", "CHF", "SEK", "NOK", "DKK", "PLN", "JPY", "INR"];

/** Offered as suggestions; a category is free text, and a person's own reappear once used. */
export const DEFAULT_CATEGORIES = [
  "Food",
  "Groceries",
  "Rent",
  "Bills",
  "Transport",
  "Travel",
  "Equipment",
  "Software",
  "Health",
  "Fun",
  "Salary",
  "Freelance",
];

/**
 * Largest single amount, in minor units: ten billion in whole currency. Past
 * this it is a typo, and past 2^53 it would stop being an exact integer at all.
 */
export const AMOUNT_MAX_MINOR = 1_000_000_000_000;

/** Whether `code` is a currency this runtime can format. */
export function isCurrency(code: unknown): code is string {
  if (typeof code !== "string" || !/^[A-Z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat("en", { style: "currency", currency: code });
    return Intl.supportedValuesOf("currency").includes(code);
  } catch {
    return false;
  }
}

/** How many minor units a currency has after the point: 2 for GBP, 0 for JPY. */
export function currencyDigits(code: string): number {
  return new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
}

/**
 * What someone typed, in minor units, or null when it is not an amount.
 *
 * Takes "12", "12.5", "1,234.56" and "£12" — the thousands separator and any
 * symbol are dropped. Refuses more decimals than the currency has rather than
 * rounding them: "12.345" in pounds is a mistake worth pointing out.
 */
export function parseAmount(text: string, digits: number): number | null {
  // The symbol is dropped but a minus is not: "-5" silently becoming 5 would
  // record the opposite of what was meant. `kind` says which way it went.
  const cleaned = text.trim().replace(/[\s,]/g, "").replace(/^[^\d.-]+/, "");
  const match = /^(\d+)(?:\.(\d*))?$/.exec(cleaned);
  if (!match) return null;
  const [, whole, frac = ""] = match;
  if (frac.length > digits) return null;
  const minor = Number(whole) * 10 ** digits + Number(frac.padEnd(digits, "0") || "0");
  return Number.isSafeInteger(minor) && minor > 0 && minor <= AMOUNT_MAX_MINOR ? minor : null;
}

/** An amount in minor units as money, in the reader's own number style. */
export function formatAmount(minor: number, currency: string | null, locale?: string): string {
  if (!currency) return (minor / 100).toFixed(2);
  const digits = currencyDigits(currency);
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 10 ** digits);
}

/** The amount as it would be typed back into the form: "1234.50", no symbol. */
export function amountText(minor: number, digits: number): string {
  return (minor / 10 ** digits).toFixed(digits);
}

/** Whether `date` is a real `YYYY-MM-DD` day — 2026-02-30 is not. */
export function isIsoDay(date: unknown): date is string {
  if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d && y >= 1900 && y <= 2200;
}

/** Today in the reader's own timezone, as the form's default date. */
export function todayIso(now = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** What is wrong with a ledger line, or null. The one rule the form and the route both run. */
export function ledgerProblem(input: Partial<LedgerInput>): string | null {
  if (!(LEDGER_KINDS as readonly string[]).includes(input.kind as string)) {
    return `kind must be one of ${LEDGER_KINDS.join(", ")}`;
  }
  const amount = input.amountMinor;
  if (typeof amount !== "number" || !Number.isSafeInteger(amount) || amount <= 0) {
    return "amount must be a positive whole number of minor units (pence, cents)";
  }
  if (amount > AMOUNT_MAX_MINOR) return "that amount is too large";
  if (!isIsoDay(input.date)) return "date must be a real day, as YYYY-MM-DD";
  if (input.category !== undefined && typeof input.category !== "string") return "category must be text";
  if ((input.category ?? "").trim().length > LEDGER_CATEGORY_MAX) {
    return `category cannot be longer than ${LEDGER_CATEGORY_MAX} characters`;
  }
  if (input.note !== undefined && typeof input.note !== "string") return "note must be text";
  if ((input.note ?? "").trim().length > LEDGER_NOTE_MAX) return `note cannot be longer than ${LEDGER_NOTE_MAX} characters`;
  return null;
}

// ── regular payments ────────────────────────────────────────────────────────

/**
 * The most lines one regular payment may post at once. Creating one with a
 * start in the past fills in what has already been paid, which is the point;
 * a start typed as 1926 instead of 2026 is not, and would bury a year in rent.
 */
export const RECURRING_BACKFILL_MAX = 120;

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * The `k`th payment of a series, counting the first as 0. It falls on the
 * start's day of the month, or on the last day of a month too short for it:
 * rent due on the 31st is paid on 28 February, and on the 31st again in March.
 */
export function recurringDay(start: string, everyMonths: number, k: number): string {
  const [y, m, d] = start.split("-").map(Number) as [number, number, number];
  const index = m - 1 + k * everyMonths;
  const year = y + Math.floor(index / 12);
  const month = ((index % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return `${year}-${pad2(month + 1)}-${pad2(Math.min(d, last))}`;
}

type Schedule = Pick<Recurring, "start" | "everyMonths" | "until" | "postedThrough">;

/**
 * The days a regular payment is owed a line for: due by `today`, not after
 * `until`, and later than anything already posted. Oldest first. At most
 * `limit` + 1 are returned, so a caller can tell "too many" from "exactly enough".
 */
export function dueDays(r: Schedule, today: string, limit = RECURRING_BACKFILL_MAX): string[] {
  const out: string[] = [];
  for (let k = 0; ; k++) {
    const day = recurringDay(r.start, r.everyMonths, k);
    if (day > today || (r.until && day > r.until)) break;
    if (!r.postedThrough || day > r.postedThrough) out.push(day);
    if (out.length > limit) break;
  }
  return out;
}

/** The next day one falls due after everything posted so far, or null once it has ended. */
export function nextDue(r: Schedule): string | null {
  for (let k = 0; ; k++) {
    const day = recurringDay(r.start, r.everyMonths, k);
    if (r.until && day > r.until) return null;
    if (!r.postedThrough || day > r.postedThrough) return day;
  }
}

/** What a regular payment comes to per month: a yearly £600 is £50. Rounded to the minor unit. */
export const perMonth = (r: Pick<Recurring, "amountMinor" | "everyMonths">): number => Math.round(r.amountMinor / r.everyMonths);

export function everyLabel(everyMonths: number): string {
  return everyMonths === 1 ? "Monthly" : everyMonths === 3 ? "Quarterly" : everyMonths === 12 ? "Yearly" : `Every ${everyMonths} months`;
}

/** What is wrong with a regular payment, or null. The form and the route both run it. */
export function recurringProblem(input: Partial<RecurringInput>): string | null {
  const line = ledgerProblem({ ...input, date: input.start });
  if (line) return line.replace(/^date /, "start ");
  if (!(RECURRING_EVERY as readonly unknown[]).includes(input.everyMonths)) {
    return `everyMonths must be one of ${RECURRING_EVERY.join(", ")}`;
  }
  if (input.until !== undefined && input.until !== null) {
    if (!isIsoDay(input.until)) return "until must be a real day, as YYYY-MM-DD";
    if (input.until < input.start!) return "it cannot end before its first payment";
  }
  return null;
}

export interface Totals {
  income: number;
  expense: number;
  /** income − expense; negative when more went out than came in. */
  net: number;
  count: number;
}

const zero = (): Totals => ({ income: 0, expense: 0, net: 0, count: 0 });

function add(t: Totals, kind: LedgerKind, amount: number): void {
  t[kind] += amount;
  t.net += kind === "income" ? amount : -amount;
  t.count += 1;
}

export interface LedgerSummary {
  total: Totals;
  /** `YYYY-MM` → totals, newest month first. Only months with something in them. */
  byMonth: { month: string; totals: Totals }[];
  /** Spending per category, largest first. Uncategorised lines are under "". */
  expenseByCategory: { category: string; amount: number }[];
  /** Earning per category, largest first. */
  incomeByCategory: { category: string; amount: number }[];
}

/** Add up a set of lines. Pure, so the view and the tests agree on every total. */
export function summarise(entries: Iterable<LedgerEntry>): LedgerSummary {
  const total = zero();
  const months = new Map<string, Totals>();
  const spent = new Map<string, number>();
  const earned = new Map<string, number>();
  for (const e of entries) {
    add(total, e.kind, e.amountMinor);
    const month = e.date.slice(0, 7);
    let m = months.get(month);
    if (!m) months.set(month, (m = zero()));
    add(m, e.kind, e.amountMinor);
    const bucket = e.kind === "expense" ? spent : earned;
    bucket.set(e.category, (bucket.get(e.category) ?? 0) + e.amountMinor);
  }
  const ranked = (map: Map<string, number>): { category: string; amount: number }[] =>
    [...map].map(([category, amount]) => ({ category, amount })).sort((a, b) => b.amount - a.amount || a.category.localeCompare(b.category));
  return {
    total,
    byMonth: [...months].sort(([a], [b]) => b.localeCompare(a)).map(([month, totals]) => ({ month, totals })),
    expenseByCategory: ranked(spent),
    incomeByCategory: ranked(earned),
  };
}

/** Newest first, and the most recently entered first within a day. */
export function compareEntries(a: LedgerEntry, b: LedgerEntry): number {
  return b.date.localeCompare(a.date) || b.createdAt - a.createdAt || a.id.localeCompare(b.id);
}

/**
 * One CSV field. Quoted when it has to be, and a leading `= + - @` is defused
 * with an apostrophe: a note of "=HYPERLINK(...)" must not become a live formula
 * in whatever spreadsheet the export is opened in.
 */
export function csvField(value: string | number): string {
  let text = String(value);
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** A whole ledger as CSV, oldest first, amounts as plain decimals with income positive. */
export function ledgerCsv(entries: LedgerEntry[], currency: string | null): string {
  const digits = currency ? currencyDigits(currency) : 2;
  const rows = [...entries].sort((a, b) => -compareEntries(a, b));
  const lines = [["date", "kind", "category", "note", `amount${currency ? ` (${currency})` : ""}`].join(",")];
  for (const e of rows) {
    const signed = (e.kind === "income" ? "" : "-") + amountText(e.amountMinor, digits);
    lines.push([csvField(e.date), csvField(e.kind), csvField(e.category), csvField(e.note), signed].join(","));
  }
  return lines.join("\r\n") + "\r\n";
}
